import React, {
  createContext,
  useState,
  useEffect,
} from 'react';
import { useLocation } from '@gatsbyjs/reach-router';
import { navigate } from 'gatsby';
import { useQuery } from '@apollo/client';
import qs from 'query-string';
import useStockPage from '../../hooks/use-stock-page';
import useVehiclesToShow from './hooks/use-vehicles-to-show';
import GET_LIVE_VEHICLES from './query';
import staticWhere from '../../where';

export const Context = createContext(null);

const keys = {
  price_min: 'lowestPrice_gte',
  price_max: 'lowestPrice_lt',
  buildyear_min: 'buildyear_gte',
  buildyear_max: 'buildyear_lt',
  odometer_min: 'odometer_gte',
  odometer_max: 'odometer_lt',
  max_towing_min: 'max_towing_gte',
  max_towing_max: 'max_towing_lt',
};

const Provider = ({
  staticVehicles = [],
  limit = 10,
  defaultSort = 'make:asc',
  defaultValues = {},
  vehiclesConnection,
  replaceUrl = true,
  possibleLayouts = ['column', 'row'],
  layoutKey = 'search',
  slugPrefix = '',
  children,
}) => {
  const stockPage = useStockPage();
  const location = useLocation();
  // fix indifference in trailing slash
  const currentPathname = `${location.pathname}${!location.pathname.endsWith('/') ? '/' : ''}`;
  const {
    sort: sortParam,
    ...searchParams
  } = qs.parse(location.search);

  const {
    fbclid, // remove this from the url
    ...searchFields
  } = searchParams;

  const [sort, setSort] = useState(sortParam || defaultSort);
  const [where, setWhere] = useState({
    ...searchFields,
  });

  const queryString = qs.stringify({
    // we dont need to save the default value, keep the qs clean
    sort: sort === defaultSort ? undefined : sort,
    ...where,
  });

  useEffect(() => {
    if (!replaceUrl) {
      return;
    }
    const newPathname = `${currentPathname}${queryString ? `?${queryString}` : ''}`;
    navigate(newPathname, { replace: true });
  }, [
    replaceUrl,
    currentPathname,
    queryString,
  ]);

  const resetFilters = () => {
    setWhere({});
  };

  useEffect(() => {
    if (!replaceUrl) {
      return;
    }
    // if we are replacing the url,
    // the search is empty
    // but we still have filters?
    // probably clicked a link to the same page
    if (!location.search) {
      setSort(defaultSort);
      resetFilters();
    }
  }, [
    replaceUrl,
    defaultSort,
    location.search,
  ]);

  // Fetch live data
  const {
    loading,
    fetchMore,
    error,
    data,
  } = useQuery(GET_LIVE_VEHICLES, {
    variables: {
      limit,
      start: 0,
      sort,
      where: Object.entries({
        ...staticWhere,
        ...defaultValues,
        ...where,
      }).reduce((acc, [key, value]) => ({
        ...acc,
        // this failed on booleans
        // [keys[key] || key]: value?.length ? value : undefined,
        // do we actually need the length check?
        [keys[key] || key]: value || undefined,
      }), {}),
    },
    notifyOnNetworkStatusChange: true,
  });

  if (error) {
    console.error(error);
    if (typeof Sentry !== 'undefined') {
      Sentry.captureException(error, (scope) => {
        scope.clear();
        scope.setContext('where', where);
        return scope;
      });
    }
  }

  const currentLoadedVehicles = data?.vehicles?.length ?? 0;

  // call this function to load more results for this query
  const loadMoreVehicles = () => fetchMore({
    variables: {
      start: currentLoadedVehicles,
    },
    updateQuery: (prev, { fetchMoreResult }) => {
      if (!fetchMoreResult) {
        return prev;
      }
      return {
        ...prev,
        vehicles: [
          ...prev.vehicles,
          ...fetchMoreResult.vehicles,
        ],
      };
    },
  });

  const { vehiclesToShow, showLoading, totalNumberOfVehicles } = useVehiclesToShow({
    staticVehicles,
    staticCount: vehiclesConnection?.aggregate?.count,
    queryString,
    vehicles: data?.vehicles,
    count: data?.vehiclesConnection?.aggregate?.count,
    loading,
  });

  if (!loading && totalNumberOfVehicles === 0) {
    console.info('Zero vehicles have been found');
    // Sentry.withScope(function(scope) {
    //   Object.keys(filterState).forEach(key => {
    //     scope.setExtra(key, filterState[key]);
    //   });
    //   scope.setLevel('info');
    //   Sentry.captureException(new Error('Got 0 vehicles from server'));
    // });
  }

  // feature layout:
  // this only works in the frontend
  // maybe one day we can read a cookie in SSR?
  const [layout, setLayout] = useState(possibleLayouts[0]);
  const key = `@vehicles/layout/${layoutKey}`;

  useEffect(() => {
    const storedLayout = window.sessionStorage.getItem(key);
    if (storedLayout && possibleLayouts.includes(storedLayout)) {
      setLayout(storedLayout);
    }
  }, [
    key,
    possibleLayouts,
  ]);

  useEffect(() => {
    window.sessionStorage.setItem(key, layout);
  }, [
    key,
    layout,
  ]);

  const value = {
    data,
    vehicles: vehiclesToShow,
    loading,
    showLoading,
    keys,
    where,
    defaultValues,
    setWhere,
    to: `${stockPage.slug}${queryString ? `?${queryString}` : ''}`,

    vehiclesConnection, // this contains filter
    slugPrefix,

    sort,
    setSort,

    resetFilters,
    loadMoreVehicles,
    totalNumberOfVehicles,
    moreVehiclesAvailable: (totalNumberOfVehicles ?? 0) - currentLoadedVehicles,

    layout,
    setLayout,
  };

  return (
    <Context.Provider value={value}>
      {children}
    </Context.Provider>
  );
};

export default Provider;
